import { CalAdminApiError } from "@/lib/cal/admin";
import { CalApiError } from "@/lib/cal/types";

/**
 * Classifiers for Cal.com errors. Cal surfaces failures through two error
 * shapes — `CalApiError` (REST API, carries `statusCode` + `message`) and
 * `CalAdminApiError` (Admin API, carries `errorCode`/`errorMessage`) — so each
 * classifier inspects both. Prefer these over re-matching message substrings at
 * the call site (see "String Handling" in the root AGENTS.md).
 */

/** Lowercased `errorCode errorMessage` text for an Admin API error. */
function adminErrorText(error: CalAdminApiError): string {
  return `${error.errorCode ?? ""} ${error.errorMessage ?? ""}`.toLowerCase();
}

/**
 * True when a Cal.com error indicates the target resource (schedule, managed
 * user, etc.) was not found — e.g. so a caller can treat a delete as already
 * done, or recreate a missing schedule.
 */
export function isCalNotFoundError(error: unknown): boolean {
  if (error instanceof CalApiError) {
    return error.statusCode === 404 || error.message.toLowerCase().includes("not found");
  }

  if (error instanceof CalAdminApiError) {
    const text = adminErrorText(error);
    // Cal.com returns various codes/messages for not-found users.
    return ["not_found", "not found", "user not found", "404"].some((token) =>
      text.includes(token),
    );
  }

  return false;
}

/**
 * True when Cal.com refused a create because the resource already exists (HTTP 409). A
 * concurrent request that created the same managed user first is the usual cause.
 */
export function isCalConflictError(error: unknown): boolean {
  return error instanceof CalApiError && error.statusCode === 409;
}

/**
 * True when a Cal.com lookup failed because the email filter itself was invalid
 * (as opposed to a transient/other failure), so a caller can skip that candidate
 * rather than abort the whole lookup.
 */
export function isCalInvalidEmailError(error: unknown): boolean {
  if (error instanceof CalApiError) {
    return error.statusCode === 400 && error.message.toLowerCase().includes("invalid email");
  }

  if (error instanceof CalAdminApiError) {
    const text = adminErrorText(error);
    return ["invalid_email", "invalid email"].some((token) => text.includes(token));
  }

  return false;
}

/**
 * True when retrying with freshly issued Cal credentials can plausibly fix the
 * failure. Generic Error messages are intentionally excluded: many validation
 * and product-policy errors mention auth words without being token failures.
 */
export function isCalRefreshableAuthError(error: unknown): boolean {
  if (error instanceof CalApiError) {
    return error.statusCode === 401 || error.statusCode === 403;
  }

  return false;
}

/**
 * True when a Cal.com credential-check response says the stored external-calendar credential is
 * definitively unusable, as opposed to the check itself having failed transiently.
 *
 * The distinction is the whole point. `/calendars/<integration>/check` answers a revoked or
 * missing grant by *throwing*, and so does a rate limit or a 5xx. Treating every throw as
 * "credential is dead" would banner a healthy provider the first time Cal has a bad minute; only
 * a definitive answer may downgrade a calendar to broken.
 *
 * Observed definitive messages: `Invalid office 365 calendar credentials.`,
 * `Invalid google OAuth credentials.`, `Credentials for google_calendar not found.`
 */
export function isCalCredentialDefinitivelyUnusableError(error: unknown): boolean {
  if (!(error instanceof CalApiError)) {
    return false;
  }

  // A 429 or 5xx says nothing about the credential.
  if (error.statusCode >= 500 || error.statusCode === 429) {
    return false;
  }

  const message = error.message.toLowerCase();
  // Substring matching is unavoidable here: Cal returns no machine-readable code on this
  // endpoint, only prose. Keep it to the two shapes it actually emits, and keep the transient
  // status-code guard above ahead of it so a 500 whose body happens to say "credentials" cannot
  // reach this line.
  const namesCredentials = message.includes("credential");
  return namesCredentials && (message.includes("invalid") || message.includes("not found"));
}

/**
 * True when a Cal.com schedule read or write failed because the schedule no longer exists.
 * Reads every status field a Cal error shape can carry before it falls back to the message.
 */
export function isMissingCalScheduleError(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }

  if ("status" in error && error.status === 404) {
    return true;
  }

  if ("statusCode" in error && error.statusCode === 404) {
    return true;
  }

  const code = "code" in error ? error.code : undefined;
  if (code === "NotFound") {
    return true;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes("not found") || message.includes("404");
  }

  return false;
}

/**
 * True when Cal.com refused a schedule call because the acting user does not own that schedule,
 * so a caller can retry with the schedule owner's credentials.
 */
export function isCalScheduleOwnershipError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return error.message.toLowerCase().includes("does not own schedule");
}
